export const SUPPORTED_LOCALES = [
  'en',
  'es',
  'fr',
  'de',
  'tr',
  'pt-BR',
  'vi',
] as const;

export type WidgetLocale = (typeof SUPPORTED_LOCALES)[number];

export type ResolvedWidgetLocale = {
  locale: WidgetLocale;
  browserLanguage: string;
  isFallback: boolean;
};

const BASE_TO_LOCALE: Record<string, WidgetLocale> = {
  en: 'en',
  es: 'es',
  fr: 'fr',
  de: 'de',
  tr: 'tr',
  vi: 'vi',
  pt: 'pt-BR',
};

/** Map one BCP 47 tag to a supported widget locale, or null if unsupported. */
export function matchWidgetLocale(tag: string): WidgetLocale | null {
  const trimmed = tag.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();

  for (const locale of SUPPORTED_LOCALES) {
    if (locale.toLowerCase() === lower) return locale;
  }

  const base = lower.split('-')[0] ?? '';
  return BASE_TO_LOCALE[base] ?? null;
}

/**
 * Pick a widget locale from an ordered browser language list.
 * Exact matches win (case-insensitive); otherwise the base language is mapped
 * (any `pt-*` → `pt-BR`). Unsupported tags (e.g. Chinese variants) are skipped.
 */
export function resolveWidgetLocale(
  languages: readonly string[]
): ResolvedWidgetLocale {
  const browserLanguage = languages[0] ?? 'en';

  let locale: WidgetLocale | null = null;
  for (const language of languages) {
    const match = matchWidgetLocale(language);
    if (match) {
      locale = match;
      break;
    }
  }

  if (!locale) {
    return { locale: 'en', browserLanguage, isFallback: true };
  }

  const firstMatch = languages[0] ? matchWidgetLocale(languages[0]) : null;
  return {
    locale,
    browserLanguage,
    isFallback: firstMatch !== locale,
  };
}

/** Safe read of `navigator.languages` for browser, SSR, and tests. */
export function getNavigatorLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    return [...navigator.languages];
  }
  if (typeof navigator.language === 'string' && navigator.language) {
    return [navigator.language];
  }
  return [];
}
